import { useState } from "react";
import { Info, CaretDown, Question } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { GLOSSARY, SCORES, PAGES } from "@/content/explainability";

// Hoverable/clickable glossary term
export const Term = ({ children, term }) => {
  const def = GLOSSARY[term || children];
  if (!def) return <span>{children}</span>;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid={`term-${(term || children).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className="underline decoration-dotted decoration-slate-400 underline-offset-2 hover:decoration-slate-900 cursor-help">
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" side="top">
        <p className="text-[11px] font-bold text-slate-900 uppercase tracking-wider">{term || children}</p>
        <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{def}</p>
      </PopoverContent>
    </Popover>
  );
};

// ⓘ score methodology popover — attach next to any score
export const ScoreInfo = ({ scoreId, light = false }) => {
  const s = SCORES[scoreId];
  if (!s) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid={`score-info-${scoreId}`} title="How is this calculated?"
          className={`inline-flex align-middle cursor-help ${light ? "text-white/40 hover:text-white/80" : "text-slate-300 hover:text-slate-700"}`}>
          <Info size={15} weight="bold" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] max-h-[70vh] overflow-y-auto p-0" side="bottom" align="start">
        <div className="px-4 py-3 bg-slate-900 text-white">
          <p className="text-xs font-bold">{s.label}</p>
          <p className="text-[11px] text-white/60 mt-1 leading-relaxed">{s.measures}</p>
        </div>
        <div className="px-4 py-3 space-y-3">
          <div>
            <p className="overline-label text-slate-400">Why it matters</p>
            <p className="text-[11px] text-slate-600 mt-0.5 leading-relaxed">{s.why}</p>
          </div>
          <div>
            <p className="overline-label text-slate-400">How it's calculated</p>
            <p className="text-[11px] text-slate-600 mt-0.5 leading-relaxed">{s.calculation}</p>
            <div className="mt-1.5 space-y-1">
              {s.components.map((c, i) => (
                <div key={i} className="flex items-start justify-between gap-3 text-[11px]">
                  <span className="text-slate-600">{c.name}</span>
                  <span className="font-mono-data font-bold text-slate-800 shrink-0">{c.weight}</span>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5">Range: {s.range}</p>
          </div>
          <div>
            <p className="overline-label text-slate-400">How to interpret</p>
            <div className="mt-1 space-y-1.5">
              {s.bands.map((b, i) => (
                <div key={i} className="text-[11px] leading-relaxed">
                  <span className="font-mono-data font-bold text-slate-800">{b.range}</span>
                  <span className="font-semibold text-slate-700"> · {b.label}</span>
                  <span className="text-slate-500"> — {b.meaning}</span>
                  <span className="block text-slate-600 pl-2 border-l-2 border-slate-200 mt-0.5 ml-0.5">→ {b.action}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="overline-label text-slate-400">Confidence</p>
            <p className="text-[11px] text-slate-600 mt-0.5 leading-relaxed">{s.confidence}</p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Full platform glossary — browsable from the sidebar
export const GlossaryDialog = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={onClose}>
      <div data-testid="glossary-dialog" onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-2xl max-h-[80vh] flex flex-col border border-slate-200 shadow-2xl">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between shrink-0">
          <div>
            <p className="overline-label text-slate-400">MAI Matrix™ Reference</p>
            <p className="font-heading font-bold text-base tracking-tight">Market Access Glossary</p>
          </div>
          <button data-testid="glossary-close" onClick={onClose} className="text-slate-400 hover:text-slate-900 text-xl leading-none px-2">×</button>
        </div>
        <div className="overflow-y-auto divide-y divide-slate-100">
          {Object.entries(GLOSSARY).map(([term, def]) => (
            <div key={term} className="px-6 py-3">
              <p className="text-xs font-bold text-slate-900">{term}</p>
              <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{def}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Purpose banner + "How this Works" — rendered automatically by PageShell for any registered page
export const PageGuide = ({ pageId }) => {
  const p = PAGES[pageId];
  const [open, setOpen] = useState(false);
  if (!p) return null;
  return (
    <div data-testid={`page-guide-${pageId}`} className="bg-white border border-slate-200 border-l-4 border-l-slate-900">
      <div className="px-5 py-3.5">
        <p className="text-xs text-slate-700 leading-relaxed">
          <span className="font-bold text-slate-900">{p.question}</span>{" "}{p.purpose}
        </p>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-2.5">
          <p className="text-[10px] text-slate-400">
            <span className="uppercase tracking-wider font-semibold">For:</span> {p.personas.join(" · ")}
          </p>
          <p className="text-[10px] text-slate-400">
            <span className="uppercase tracking-wider font-semibold">Decisions:</span> {p.decisions.join(" · ")}
          </p>
          {p.terms?.length > 0 && (
            <p className="text-[10px] text-slate-400">
              <span className="uppercase tracking-wider font-semibold">Key terms:</span>{" "}
              {p.terms.map((t, i) => <span key={t}>{i > 0 && " · "}<Term term={t}>{t}</Term></span>)}
            </p>
          )}
          <button data-testid={`how-it-works-toggle-${pageId}`} onClick={() => setOpen(!open)}
            className="ml-auto text-[10px] font-semibold text-slate-600 hover:text-slate-900 flex items-center gap-1 uppercase tracking-wider">
            <Question size={12} weight="bold" /> How this works
            <CaretDown size={10} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>
      {open && (
        <div data-testid={`how-it-works-panel-${pageId}`} className="px-5 py-4 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 bg-slate-50/60">
          {[
            ["Inputs", p.how.inputs],
            ["Processing", p.how.processing],
            ["Outputs", p.how.outputs],
            ["How to interpret", p.how.interpret],
          ].map(([label, text]) => (
            <div key={label}>
              <p className="overline-label text-slate-400">{label}</p>
              <p className="text-[11px] text-slate-600 mt-1 leading-relaxed">{text}</p>
            </div>
          ))}
          <div className="md:col-span-2 xl:col-span-3">
            <p className="overline-label text-slate-400">What to look for</p>
            <ul className="mt-1 space-y-0.5">
              {p.look_for.map((l, i) => (
                <li key={i} className="text-[11px] text-slate-600 leading-relaxed flex gap-1.5"><span className="text-slate-300">›</span>{l}</li>
              ))}
            </ul>
          </div>
          <div>
            <p className="overline-label text-slate-400">Then</p>
            <p className="text-[11px] text-slate-600 mt-1 leading-relaxed">{p.how.next}</p>
          </div>
        </div>
      )}
    </div>
  );
};
